import React, { useState, useRef, useEffect, useCallback } from 'react';
import clsx from 'clsx';

import APDropdown from 'components/Dropdown/APDropdown';
import FormItem from 'components/FormFields/FormItem';
import SubmitInputField from 'components/FormFields/SubmitInputField';

import './autocomplete.scss';

/**
 * Input element that filters through provided options. This form element is uncontrolled.
 * @param {*} props
 * @returns
 */
const FormAutocomplete = (props) => {
  const {
    id,
    name,
    value,
    onSubmit,
    onInputChange,
    options = [],
    renderOption,
    required = false,
    submitOnBlur = false,
    error,
    className,
    labelProps,
    labelHidden,
    wrapperProps,
    dropdownProps,
    ...rest
  } = props;

  // Strip special chars and replace spaces with - for id.
  const elementId = id ?? name.replace(/[^a-zA-Z0-9 ]/g, '').replace(' ', '-').toLowerCase();

  const { className: wrapperClasses, ...restWrapper } = wrapperProps || {};
  const { className: dropdownClasses, ...restDropdown } = dropdownProps || {};

  const inputRef = useRef();
  const selectedRef = useRef(null);

  const [isOpen, setIsOpened] = useState(false);

  const show = () => {
    setIsOpened(true);
  };

  const dismiss = () => {
    setIsOpened(false);
  };

  const handleFocus = () => {
    show();
  };

  const handleInputSubmit = () => {
    if (!inputRef.current) return;
    onSubmit(inputRef.current.value);
    dismiss();
  };

  const handleOptionClick = () => {
    dismiss();
  };

  const handleBlur = (e) => {
    if (e) handleInputSubmit();
  };

  const renderOptionCallback = useCallback(renderOption, [options]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = value;
    }
  }, [value]);

  return (
    <FormItem
      id={elementId}
      name={name}
      labelProps={labelProps}
      type="autocomplete"
      className={wrapperClasses}
      labelHidden={labelHidden}
      required={required}
      {...restWrapper}
    >
      <>
        <SubmitInputField
          ref={inputRef}
          defaultValue={value}
          className={clsx('form-item__input', className, error && 'error')}
          type="text"
          onFocus={handleFocus}
          handleSubmit={handleInputSubmit}
          onBlur={submitOnBlur ? handleBlur : null}
          onChange={onInputChange}
          {...rest}
        />
        {error && (
          <div
            className="form-item__error"
          >
            {error}
          </div>
        )}
        {inputRef.current && (
          <APDropdown
            isOpen={isOpen}
            show={show}
            dismiss={dismiss}
            className={clsx(dropdownClasses)}
            root={null}
            anchorElement={inputRef.current}
            dropDownClass='autocomplete-dropdown__menu'
            menuClass="autocomplete-menu"
            isFullWidth
            placement='bottom-start'
            selectedRef={selectedRef}
            {...restDropdown}
          >
            {(Array.isArray(options) && renderOption) ?
              options.map((opt, i) => {
                const selected = opt?.selected ?? false;

                return (
                  <div
                    key={`autocomplete-option-${i}`}
                    ref={selected ? selectedRef : null}
                    onClick={handleOptionClick}
                    className="autocomplete__option"
                  >
                    {renderOptionCallback(opt, i)}
                  </div>
                );
              }) : null
            }
          </APDropdown>
        )}
      </>
    </FormItem>
  );
};

export default FormAutocomplete;
